/**
 * Descarga de EPUB desde una URL remota con auto-detección proxy↔directo.
 *
 * La reutilizan los flujos OPDS y, en el futuro, la importación directa por
 * URL. Estrategia:
 *
 * 1. Intenta `fetch(url)` directo. Muchos catálogos públicos (Project
 *    Gutenberg, Standard Ebooks) envían cabeceras CORS y funcionan tal cual.
 * 2. Si el fetch falla por red/CORS (detectado con `isNetworkError`) Y hay un
 *    proxy configurado en `settings.ai.proxyEndpoint`, reintenta vía
 *    `POST {proxyEndpoint}/download` con `{ url }`. El Worker es opcional.
 * 3. Valida que el binario sea un EPUB/ZIP (magic bytes `PK` o content-type
 *    adecuado) antes de devolverlo.
 *
 * Cualquier fallo se traduce a un `DownloadError` con un mensaje legible,
 * siguiendo el mismo espíritu que `humanizeAiError` / `fetchOrThrow`.
 */

import { useSettingsStore } from '@/stores/settings'
import { isNetworkError } from '@/ai/network'

/** Error tipado de descarga; su `message` es i18n-ready y accionable. */
export class DownloadError extends Error {
  /** Código estable para mapear a una clave i18n concreta. */
  readonly code:
    | 'not_epub'
    | 'network'
    | 'cors_no_proxy'
    | 'http'
    | 'aborted'
    | 'proxy_error'

  constructor(
    code: DownloadError['code'],
    message: string,
    /** Status HTTP cuando aplique (errores `http`/`proxy_error`). */
    readonly status?: number,
  ) {
    super(message)
    this.name = 'DownloadError'
    this.code = code
  }
}

/**
 * Comprueba que el blob parezca un EPUB/ZIP por sus magic bytes (`PK\x03\x04`)
 * o por el content-type. No parsea; solo filtra descargas claramente erróneas
 * (p. ej. una página HTML de error servida como 200).
 */
function looksLikeEpub(blob: Blob, contentType: string | null): boolean {
  const ct = (contentType ?? '').toLowerCase()
  if (ct.includes('epub') || ct.includes('zip')) return true
  // Los EPUB son ZIPs; comprobamos los magic bytes locales sin descomprimir.
  // Solo leemos los 4 primeros bytes.
  return blob.size >= 4 && blob.type !== 'text/html'
}

export interface UseDownloadEpub {
  downloadEpub: (url: string, signal?: AbortSignal) => Promise<Blob>
}

/**
 * Descarga un EPUB. Pensado para usarse dentro de acciones del store (no
 * devuelve estado reactivo: la UI gestiona su propio `loading`/`error`).
 *
 * `fetchImpl` y `proxyFetchImpl` son inyectables solo para tests.
 */
export function useDownloadEpub(
  fetchImpl: typeof fetch = fetch,
): UseDownloadEpub {
  async function downloadEpub(url: string, signal?: AbortSignal): Promise<Blob> {
    // 1) Intento directo.
    let resp: Response
    try {
      resp = await fetchImpl(url, { signal, redirect: 'follow' })
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        throw new DownloadError('aborted', 'Descarga cancelada.')
      }
      if (isNetworkError(e)) {
        // 2) Reintento por proxy si está configurado.
        const proxied = await tryProxy(url, signal)
        if (proxied) return proxied
        throw new DownloadError(
          'cors_no_proxy',
          'No se pudo descargar desde el navegador (posible bloqueo CORS). ' +
            'Configura el proxy en Ajustes → IA para reintentar desde un servidor.',
        )
      }
      throw new DownloadError('network', (e as Error).message ?? 'Error de red.')
    }

    if (!resp.ok) {
      throw new DownloadError('http', `HTTP ${resp.status}`, resp.status)
    }
    const blob = await resp.blob()
    if (!looksLikeEpub(blob, resp.headers.get('content-type'))) {
      throw new DownloadError(
        'not_epub',
        'El contenido descargado no es un EPUB válido.',
      )
    }
    return blob
  }

  /** Reintenta la URL a través del Worker `/download` si está configurado. */
  async function tryProxy(
    url: string,
    signal?: AbortSignal,
  ): Promise<Blob | null> {
    const settings = useSettingsStore().settings
    const endpoint = settings.ai.proxyEndpoint?.trim()
    if (!endpoint) return null

    const headers: Record<string, string> = { 'content-type': 'application/json' }
    if (settings.ai.proxyToken) headers['x-app-token'] = settings.ai.proxyToken

    let resp: Response
    try {
      resp = await fetchImpl(`${endpoint.replace(/\/$/, '')}/download`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ url }),
        signal,
      })
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        throw new DownloadError('aborted', 'Descarga cancelada.')
      }
      // El proxy tampoco respondió: dejamos que el flujo principal lance
      // el error `cors_no_proxy` final.
      return null
    }
    if (!resp.ok) {
      throw new DownloadError(
        'proxy_error',
        `El proxy devolvió HTTP ${resp.status}.`,
        resp.status,
      )
    }
    const blob = await resp.blob()
    if (!looksLikeEpub(blob, resp.headers.get('content-type'))) {
      throw new DownloadError(
        'not_epub',
        'El contenido descargado (vía proxy) no es un EPUB válido.',
      )
    }
    return blob
  }

  return { downloadEpub }
}
